import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { apiService } from '../../services/api'
import { getUserType } from '../../utils/userPermissions'
import { 
  ShoppingCart, 
  Search, 
  Filter, 
  Eye, 
  Edit,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  ArrowLeft
} from 'lucide-react'
import { toast } from 'react-toastify'

interface PurchaseOrder {
  id: string
  poNumber: string
  status: string
  totalAmount: number
  currency: string
  notes?: string
  createdAt: string
  updatedAt: string
  rfq: {
    id: string
    title: string
    buyer: {
      id: string
      email: string
      firstName?: string
      lastName?: string
      companyName?: string
    }
  }
  rfqResponse: {
    id: string
    vendor: {
      id: string
      email: string
      firstName?: string
      lastName?: string
      companyName?: string
    }
  }
  buyer: {
    id: string
    email: string
    firstName?: string
    lastName?: string
    companyName?: string
  }
  vendor: {
    id: string
    email: string
    firstName?: string
    lastName?: string
    companyName?: string
  }
}

const PurchaseOrdersPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [roleFilter, setRoleFilter] = useState('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [poToCancel, setPoToCancel] = useState<{id: string, poNumber: string} | null>(null)

  useEffect(() => {
    if (!user) {
      console.log('PurchaseOrdersPage: No user found, redirecting to login')
      navigate('/auth/login')
      return
    }

    // Check if user is enterprise user
    const userType = getUserType(user)
    const isEnterpriseUser = userType === 'ENTERPRISE_BUYER' || userType === 'ENTERPRISE_VENDOR'
    
    console.log('PurchaseOrdersPage access check:')
    console.log('- User:', user)
    console.log('- User type:', userType)
    console.log('- Is enterprise user:', isEnterpriseUser)
    console.log('- User role:', user.role)
    console.log('- User isEnterprise:', user.isEnterprise)
    
    if (!isEnterpriseUser) {
      console.log('PurchaseOrdersPage: User is not enterprise, redirecting to home')
      navigate('/', { replace: true })
      return
    }

    console.log('PurchaseOrdersPage: Access granted, loading purchase orders')
    loadPurchaseOrders()
  }, [user, navigate])

  // Reload purchase orders when filters change
  useEffect(() => {
    if (user) {
      loadPurchaseOrders()
    }
  }, [statusFilter, roleFilter])

  const loadPurchaseOrders = async () => {
    try {
      setLoading(true)
      console.log('PurchaseOrdersPage: Loading purchase orders...')
      console.log('- Status filter:', statusFilter)
      console.log('- Role filter:', roleFilter)
      
      const response = await apiService.get('/api/post-quote-activity/purchase-orders', {
        params: {
          status: statusFilter !== 'all' ? statusFilter : undefined,
          role: roleFilter !== 'all' ? roleFilter : undefined
        }
      }) as any
      
      console.log('PurchaseOrdersPage: API response:', response)
      console.log('PurchaseOrdersPage: Purchase orders data:', response.data)
      
      setPurchaseOrders(response.data || [])
    } catch (error) {
      console.error('Error loading purchase orders:', error)
      console.error('Error details:', error)
      toast.error('Failed to load purchase orders')
      setPurchaseOrders([]) // Set empty array on error
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800'
      case 'ACCEPTED':
        return 'bg-green-100 text-green-800'
      case 'IN_REVIEW':
        return 'bg-blue-100 text-blue-800'
      case 'REJECTED':
        return 'bg-red-100 text-red-800'
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="w-4 h-4" />
      case 'ACCEPTED':
        return <CheckCircle className="w-4 h-4" />
      case 'IN_REVIEW':
        return <AlertCircle className="w-4 h-4" />
      case 'REJECTED':
      case 'CANCELLED':
        return <XCircle className="w-4 h-4" />
      default:
        return <Clock className="w-4 h-4" />
    }
  }

  const filteredPurchaseOrders = purchaseOrders.filter(po => {
    // Only apply search filter here since status and role filters are handled by the API
    if (!searchTerm) return true
    
    const matchesSearch = po.poNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         po.rfq.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         po.buyer.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         po.vendor.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
    
    return matchesSearch
  })

  const handleStatusUpdate = async (poId: string, newStatus: string) => {
    // Show confirmation modal for buyer cancelling PO
    if (newStatus === 'CANCELLED' && user?.id === purchaseOrders.find(po => po.id === poId)?.buyer.id) {
      const po = purchaseOrders.find(po => po.id === poId)
      if (po) {
        setPoToCancel({ id: poId, poNumber: po.poNumber })
        setShowCancelConfirm(true)
        return
      }
    }
    
    await performStatusUpdate(poId, newStatus)
  }

  const performStatusUpdate = async (poId: string, newStatus: string) => {
    try {
      await apiService.patch(`/api/post-quote-activity/purchase-orders/${poId}/status`, {
        status: newStatus
      })
      
      const actionText = newStatus === 'CANCELLED' ? 'cancelled' : 
                        newStatus === 'ACCEPTED' ? 'accepted' : 
                        newStatus === 'REJECTED' ? 'rejected' : 
                        'updated'
      
      toast.success(`Purchase order ${actionText} successfully`)
      loadPurchaseOrders()
    } catch (error) {
      console.error('Error updating PO status:', error)
      toast.error('Failed to update purchase order status')
    }
  }

  const handleCancelConfirm = async () => {
    if (poToCancel) {
      await performStatusUpdate(poToCancel.id, 'CANCELLED')
      setShowCancelConfirm(false)
      setPoToCancel(null)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow-md p-6">
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/enterprise/post-quote-activity')}
                className="mr-4 p-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Purchase Orders</h1>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 w-full sm:w-auto"
            >
              <ShoppingCart className="w-4 h-4" />
              <span className="text-sm sm:text-base">Create Purchase Order</span>
            </button>
          </div>
          <p className="text-gray-600 text-sm sm:text-base">
            Manage purchase orders created from accepted RFQ responses
          </p>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search PO number, title, or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="ACCEPTED">Accepted</option>
                <option value="IN_REVIEW">In Review</option>
                <option value="REJECTED">Rejected</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Roles</option>
                <option value="buyer">As Buyer</option>
                <option value="vendor">As Vendor</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={loadPurchaseOrders}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <Filter className="w-4 h-4 mr-2" />
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        {/* Purchase Orders List */}
        <div className="space-y-4">
          {filteredPurchaseOrders.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Purchase Orders Found</h3>
              <p className="text-gray-600">
                {searchTerm || statusFilter !== 'all' || roleFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Purchase orders will appear here once created from accepted RFQ responses'
                }
              </p>
            </div>
          ) : (
            filteredPurchaseOrders.map((po) => (
              <div key={po.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(po.status)}`}>
                      {getStatusIcon(po.status)}
                      <span>{po.status}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{po.poNumber}</h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => navigate(`/enterprise/post-quote-activity/purchase-orders/${po.id}`)}
                      className="p-2 text-gray-600 hover:text-gray-900 transition-colors touch-manipulation"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => navigate(`/enterprise/post-quote-activity/purchase-orders/${po.id}/edit`)}
                      className="p-2 text-gray-600 hover:text-gray-900 transition-colors touch-manipulation"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600">RFQ Title</p>
                    <p className="text-sm text-gray-900">{po.rfq.title}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Buyer</p>
                    <p className="text-sm text-gray-900">
                      {po.buyer.companyName || `${po.buyer.firstName} ${po.buyer.lastName}`}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Vendor</p>
                    <p className="text-sm text-gray-900">
                      {po.vendor.companyName || `${po.vendor.firstName} ${po.vendor.lastName}`}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Amount</p>
                    <p className="text-sm font-semibold text-gray-900">
                      ${po.totalAmount?.toLocaleString()} {po.currency}
                    </p>
                  </div>
                </div>

                {po.notes && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-600">Notes</p>
                    <p className="text-sm text-gray-900">{po.notes}</p>
                  </div>
                )}

                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Created: {new Date(po.createdAt).toLocaleDateString()}</span>
                  <span>Updated: {new Date(po.updatedAt).toLocaleDateString()}</span>
                </div>

                {/* Status Update Actions - Role-based */}
                {user?.id === po.buyer.id || user?.id === po.vendor.id ? (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      {user?.id === po.buyer.id ? 'Buyer Actions:' : 'Vendor Actions:'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {/* Vendor Actions */}
                      {user?.id === po.vendor.id && po.status === 'PENDING' && (
                        <>
                          <button
                            onClick={() => handleStatusUpdate(po.id, 'ACCEPTED')}
                            className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                          >
                            Accept PO
                          </button>
                          <button
                            onClick={() => handleStatusUpdate(po.id, 'REJECTED')}
                            className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                          >
                            Reject PO
                          </button>
                          <button
                            onClick={() => handleStatusUpdate(po.id, 'IN_REVIEW')}
                            className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                          >
                            Mark for Review
                          </button>
                        </>
                      )}
                      
                      {/* Vendor Actions for IN_REVIEW */}
                      {user?.id === po.vendor.id && po.status === 'IN_REVIEW' && (
                        <>
                          <button
                            onClick={() => handleStatusUpdate(po.id, 'ACCEPTED')}
                            className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                          >
                            Accept PO
                          </button>
                          <button
                            onClick={() => handleStatusUpdate(po.id, 'REJECTED')}
                            className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                          >
                            Reject PO
                          </button>
                        </>
                      )}
                      
                      {/* Buyer Actions - Can only cancel */}
                      {user?.id === po.buyer.id && (po.status === 'PENDING' || po.status === 'IN_REVIEW' || po.status === 'ACCEPTED') && (
                        <button
                          onClick={() => handleStatusUpdate(po.id, 'CANCELLED')}
                          className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 transition-colors"
                        >
                          Cancel PO
                        </button>
                      )}
                      
                      {/* Status-specific messages */}
                      {user?.id === po.buyer.id && po.status === 'PENDING' && (
                        <p className="text-xs text-gray-500 mt-2">
                          Waiting for vendor to accept or reject this Purchase Order
                        </p>
                      )}
                      
                      {user?.id === po.vendor.id && po.status === 'PENDING' && (
                        <p className="text-xs text-gray-500 mt-2">
                          You can accept, reject, or mark this PO for review
                        </p>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Purchase Order Modal */}
      {showCreateModal && (
        <CreatePurchaseOrderModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false)
            loadPurchaseOrders()
          }}
        />
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && poToCancel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Cancel Purchase Order
                </h3>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-2">
                Are you sure you want to cancel this Purchase Order?
              </p>
              <p className="text-sm font-medium text-gray-900 mb-2">
                PO Number: {poToCancel.poNumber}
              </p>
              <p className="text-sm text-gray-600">
                This action cannot be undone and will notify the vendor immediately.
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCancelConfirm(false)
                  setPoToCancel(null)
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Keep PO
              </button>
              <button
                onClick={handleCancelConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Cancel PO
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Create Purchase Order Modal Component
const CreatePurchaseOrderModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    rfqResponseId: '',
    totalAmount: '',
    currency: 'USD',
    notes: ''
  })
  const [loading, setLoading] = useState(false)
  const [acceptedRfqResponses, setAcceptedRfqResponses] = useState<any[]>([])

  useEffect(() => {
    if (isOpen) {
      loadAcceptedRfqResponses()
    }
  }, [isOpen])

  const loadAcceptedRfqResponses = async () => {
    try {
      console.log('Loading buyer RFQs with accepted responses...')
      // Get buyer's RFQs with all responses
      const response = await apiService.get('/api/enterprise/rfqs/buyer/rfqs') as any
      console.log('Buyer RFQs response:', response.data)
      
      // Extract accepted responses from all RFQs
      const acceptedResponses: any[] = []
      if (response.data?.rfqs) {
        response.data.rfqs.forEach((rfq: any) => {
          if (rfq.responses) {
            rfq.responses.forEach((resp: any) => {
              if (resp.status === 'ACCEPTED') {
                // Add RFQ info to the response for display
                acceptedResponses.push({
                  ...resp,
                  rfq: {
                    id: rfq.id,
                    title: rfq.title
                  }
                })
              }
            })
          }
        })
      }
      
      console.log('Filtered accepted responses:', acceptedResponses)
      setAcceptedRfqResponses(acceptedResponses)
    } catch (error: any) {
      console.error('Error loading accepted RFQ responses:', error)
      console.error('Error response:', error.response)
      console.error('Error status:', error.response?.status)
      console.error('Error data:', error.response?.data)
      if (error.response?.status === 404) {
        toast.error('RFQ endpoint not found. Please check the API configuration.')
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Please check your permissions.')
      } else {
        toast.error('Failed to load accepted RFQ responses')
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.rfqResponseId) {
      toast.error('Please select an RFQ response')
      return
    }

    try {
      setLoading(true)
      console.log('Creating purchase order:', formData)
      console.log('API Base URL:', import.meta.env.VITE_API_URL)
      console.log('Full URL will be:', `${import.meta.env.VITE_API_URL}/api/post-quote-activity/purchase-orders`)
      
      await apiService.post('/api/post-quote-activity/purchase-orders', {
        ...formData,
        totalAmount: parseFloat(formData.totalAmount) || undefined
      })
      toast.success('Purchase Order created successfully')
      onSuccess()
    } catch (error: any) {
      console.error('Error creating purchase order:', error)
      console.error('Error response:', error.response)
      console.error('Error status:', error.response?.status)
      console.error('Error data:', error.response?.data)
      console.error('Request URL:', error.config?.url)
      toast.error(error.response?.data?.message || 'Failed to create purchase order')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">Create Purchase Order</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Accepted RFQ Response *
            </label>
            <select
              value={formData.rfqResponseId}
              onChange={(e) => {
                const selectedResponse = acceptedRfqResponses.find(r => r.id === e.target.value)
                setFormData({
                  ...formData,
                  rfqResponseId: e.target.value,
                  totalAmount: selectedResponse?.proposedPrice?.toString() || ''
                })
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select an accepted RFQ response</option>
              {acceptedRfqResponses.map((response) => (
                <option key={response.id} value={response.id}>
                  {response.rfq?.title} - {response.vendor?.companyName || response.vendor?.email} 
                  (${response.proposedPrice})
                </option>
              ))}
            </select>
            {acceptedRfqResponses.length === 0 && (
              <p className="text-sm text-gray-500 mt-1">
                No accepted RFQ responses available. Accept an RFQ response first to create a purchase order.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total Amount
            </label>
            <div className="flex">
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="border border-gray-300 rounded-l-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-20"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
              <input
                type="number"
                step="0.01"
                value={formData.totalAmount}
                onChange={(e) => setFormData({ ...formData, totalAmount: e.target.value })}
                className="flex-1 border-t border-b border-r border-gray-300 rounded-r-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
              />
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Leave blank to use the proposed price from the RFQ response
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Additional notes for this purchase order..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || acceptedRfqResponses.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Purchase Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PurchaseOrdersPage 